import { Link } from 'react-router-dom';
import type { Article } from '@/types/content';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'horizontal' | 'minimal';
}

function CategoryTag({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 text-[11px] font-medium whitespace-nowrap">
      {label}
    </span>
  );
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'minimal') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex items-start gap-4 py-4 border-b border-background-200 last:border-0"
      >
        <span className="mt-0.5 text-xs font-heading font-bold text-primary-500 w-10 shrink-0">
          {article.date.slice(5)}
        </span>
        <span className="flex-1">
          <span className="block text-sm font-medium text-foreground-900 leading-6 group-hover:text-primary-600 transition-colors line-clamp-2">
            {article.title}
          </span>
          <span className="mt-1.5 flex items-center gap-2 text-[11px] text-foreground-500">
            <span>{article.categoryLabel}</span>
            <span className="w-1 h-1 rounded-full bg-background-400"></span>
            <span>{article.readTime}</span>
          </span>
        </span>
      </Link>
    );
  }

  if (variant === 'horizontal') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex flex-col sm:flex-row gap-4 sm:gap-5 p-3 rounded-lg border border-background-200 bg-background-50 hover:border-primary-300 transition-colors"
      >
        <div className="w-full sm:w-44 h-40 sm:h-28 rounded-md overflow-hidden shrink-0 bg-background-200">
          <img
            src={article.cover}
            alt={article.title}
            title={`${article.title} - 福利吃瓜`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="flex-1 min-w-0 flex flex-col">
          <div className="flex items-center gap-2 mb-2">
            <CategoryTag label={article.categoryLabel} />
            <span className="text-[11px] text-foreground-500">{article.date}</span>
          </div>
          <h3 className="font-heading text-base font-bold text-foreground-950 leading-6 group-hover:text-primary-600 transition-colors line-clamp-2">
            {article.title}
          </h3>
          <p className="mt-1.5 text-xs text-foreground-600 leading-6 line-clamp-2 hidden sm:block">
            {article.excerpt}
          </p>
          <div className="mt-auto pt-3 flex items-center gap-3 text-[11px] text-foreground-500">
            <span>{article.author}</span>
            <span className="flex items-center gap-1">
              <i className="ri-fire-line text-primary-400" aria-hidden="true"></i>
              {article.heat}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.id}`}
      className="group flex flex-col h-full rounded-lg overflow-hidden border border-background-200 bg-background-50 hover:border-primary-300 transition-colors"
    >
      <div className="w-full h-48 overflow-hidden bg-background-200">
        <img
          src={article.cover}
          alt={article.title}
          title={`${article.title} - 福利吃瓜`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="flex-1 flex flex-col p-5">
        <div className="flex items-center gap-2 mb-3">
          <CategoryTag label={article.categoryLabel} />
          <span className="text-[11px] text-foreground-500">{article.readTime}</span>
        </div>
        <h3 className="font-heading text-lg font-bold text-foreground-950 leading-7 group-hover:text-primary-600 transition-colors line-clamp-2">
          {article.title}
        </h3>
        <p className="mt-2 text-sm text-foreground-600 leading-7 line-clamp-3">{article.excerpt}</p>
        <div className="mt-auto pt-4 flex items-center justify-between text-[11px] text-foreground-500">
          <span>{article.date}</span>
          <span className="flex items-center gap-1 text-primary-500 font-medium">
            <i className="ri-fire-line" aria-hidden="true"></i>
            {article.heat}
          </span>
        </div>
      </div>
    </Link>
  );
}